import * as React from "react";
import { Outlet, useLocation } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import * as Sentry from "@sentry/react";
import SideNav from "../../components/SideNav/SideNav";
import ScrollToTop from "../../utils/ScrollToTop";
import { useFetchDiagnosisCodes } from "../../hooks/queries";
import ContentFallback from "../../components/ErrorBoundaryFallback/ContentFallback/ContentFallback";
import { useAppSelector } from "../../hooks/useRedux";
import { useFetchPracticeSettings } from "../../modules/practice-settings/hooks/queries/practice-settings";
import { cn } from "../../utils/helpers";

export default function AppContainer() {
    const location = useLocation();
    // Fetch diagnosis codes
    useFetchDiagnosisCodes(false);

    useFetchPracticeSettings({
        category: "general",
        setting_name: "prioritize_insurance_over_self_pay",
    });

    const { isOpen } = useAppSelector((state) => state.nav);
    const allowStickyNoteContent =
        /(?:^|\/)notes\/[^/]+\/[^/]+(?:\/edit)?\/?$/.test(location.pathname);

    return (
        <div>
            <ScrollToTop scrollWindow={allowStickyNoteContent} />
            <div className="flex">
                <SideNav />
                <ErrorBoundary
                    fallback={<ContentFallback />}
                    onError={(err) => {
                        Sentry.captureException(err);
                    }}
                >
                    <div
                        className={cn(
                            "min-h-dvh ml-0 p-0 md:p-2 md:ml-[--sidebar-width] flex-1 transition-[margin_left] duration-[0.25s] ease-in-out bg-[--card-bg] max-w-full w-full overflow-x-hidden",
                            {
                                "md:ml-0 md:p-0": !isOpen,
                                "!overflow-y-visible": allowStickyNoteContent,
                                "overflow-x-hidden !overflow-y-auto":
                                    !allowStickyNoteContent,
                            }
                        )}
                        id="main"
                    >
                        <div
                            className={cn(
                                "[html[data-theme='light']_&]:bg-[#f9f9fb] [html[data-theme='h-green']_&]:bg-transparent [html[data-theme='h-green']_&]:[border:0.5px_solid_var(--color-stroke)] [html[data-theme='h-green']_&]:border-stroke min-h-full md:rounded-md bg-[#f9f9fb] max-w-full h-full",
                                {
                                    "md:rounded-none": !isOpen,
                                    "!overflow-y-visible":
                                        allowStickyNoteContent,
                                    "overflow-y-auto": !allowStickyNoteContent,
                                }
                            )}
                        >
                            <main id="main">
                                <Outlet />
                            </main>
                        </div>
                    </div>
                </ErrorBoundary>
            </div>
        </div>
    );
}
